import { useCallback, useEffect, useRef } from "react";

import { useLocalStorage } from "@bridgesplit/react";
import { Result } from "@bridgesplit/utils";
import { useSearchParams } from "react-router-dom";

import { useVerifyAndClaimSeekerRewardsMutation, useCommunityMultiplierApi } from "../reducers";
import { CommunityMultiplierReturn, CommunityMultiplierType } from "../types";
import { useSeekerGenesisToken } from "./user-tokens";

/**
 * Detects if the user is accessing the app through the PWA (Seeker).
 * Checks for `?source=pwa` URL parameter and persists to localStorage.
 *
 * Uses react-router-dom's useSearchParams for consistent param handling.
 */

const SEEKER_SOURCE_KEY = "loopscale_pwa_source";
const SEEKER_SOURCE_VALUE = "pwa";

export function useIsSeeker(): boolean {
    const [isSeeker, setIsSeeker] = useLocalStorage(SEEKER_SOURCE_KEY, false);
    const [searchParams] = useSearchParams();
    const hasRunRef = useRef(false);

    useEffect(() => {
        if (hasRunRef.current) return;
        if (typeof window === "undefined" || isSeeker) {
            hasRunRef.current = true;
            return;
        }

        const source = searchParams.get("source");
        if (source === SEEKER_SOURCE_VALUE) {
            setIsSeeker(true);
        }
        hasRunRef.current = true;
    }, [searchParams, isSeeker, setIsSeeker]);

    return isSeeker;
}

const SGT_VERIFICATION_ERRORS = {
    NO_VALID_TOKEN: "No valid Seeker Genesis Token found in wallet",
    VERIFICATION_FAILED: "Failed to verify Seeker Genesis Token"
};

export function useVerifySeekerGenesisToken(communityMultipliers: CommunityMultiplierReturn[]) {
    const { isVerifying, verify } = useSeekerGenesisToken();
    const [verifyAndClaimSeeker] = useVerifyAndClaimSeekerRewardsMutation();
    const { resetCommunityMultipliers } = useCommunityMultiplierApi();

    const hasClaimed = isSeekerMultiplierAlreadyClaimed(communityMultipliers);

    const handleSeekerVerifyAndClaim = useCallback(async () => {
        try {
            // 1. Local RPC verification
            const result = await verify();

            if (!result?.isValid) {
                return Result.err(SGT_VERIFICATION_ERRORS.NO_VALID_TOKEN, { skipSentry: true });
            }

            // 2. Backend verification + claim
            await verifyAndClaimSeeker({ sgtMint: result.mintAddress }).unwrap();

            resetCommunityMultipliers();
            return Result.ok(true);
        } catch (rawError) {
            const errorResult = Result.err(rawError);
            const message = errorResult.unwrapErrMessage(SGT_VERIFICATION_ERRORS.VERIFICATION_FAILED);
            return Result.errFromMessage(message, { skipSentry: true });
        }
    }, [verify, verifyAndClaimSeeker, resetCommunityMultipliers]);
    return {
        hasClaimed,
        isVerifying,
        handleSeekerVerifyAndClaim
    };
}

function isSeekerMultiplierAlreadyClaimed(communityMultipliers: CommunityMultiplierReturn[]): boolean {
    return communityMultipliers.some((m) => m.communityMultiplier === CommunityMultiplierType.SkrGenesis);
}
